/**
 * DtrReportPage — the DTR report generator (PRD_DtrReport C4, Step 5; range
 * controls per PRD_DtrDateRange).
 *
 * A GENERATOR, not a second viewer: preset chips + a FROM/TO date pair (C1,
 * C3), a scope selector (All employees / By department / Single employee) and
 * a Generate button. It never re-renders the range's rows — the Employee Time
 * Records page owns that view, with its own incomplete/no-entries filters as
 * the fix path.
 *
 * Reads (C17, as amended by PRD_DtrDateRange C7): one roster query on mount
 * (role == 'employee', single-field, no composite index) which returns each
 * employee's full document — so name and department come free — then one
 * getDoc per employee per month document the range spans (1-3, resolved by
 * getMonthsInRange), nested-parallel. Nothing is written.
 */
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import {
  differenceInCalendarDays,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subWeeks,
  subMonths,
} from 'date-fns';
import { db } from '../../firebase/config';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import {
  todayManilaYMD,
  parsePeriodToDate,
  toYMDString,
  getMonthsInRange,
} from '../../utils/periodDate';
import { buildDtrModel, isEmployedInRange } from '../../services/dtrModel';
import { loadDtrPdf, PDF_LOAD_ERROR } from '../../services/pdfLoader';
import { usePdfPreview } from '../../hooks/usePdfPreview';
import PdfPreviewPanel from './PdfPreviewPanel';

/**
 * C5's three blocking cases, in precedence order — empty must win over the
 * length math, because an empty bound parses to null, which date-fns
 * normalizes to the 1970 epoch: the measured "length" of an empty-FROM range
 * is ±20,000-odd days, not NaN, so the cap message would show beside an empty
 * box and read as a bug. Inverted compares on 'YYYY-MM-DD' STRINGS; only the
 * C4 day-count parses, via parsePeriodToDate (never raw new Date()).
 * Exported for the boundary-exact unit cases (PRD Verification).
 */
export const rangeError = (fromYMD, toYMD) => {
  if (!fromYMD || !toYMD) return 'Select a start and end date.';
  if (toYMD < fromYMD) return 'End date cannot be before start date.';
  const days =
    differenceInCalendarDays(parsePeriodToDate(toYMD), parsePeriodToDate(fromYMD)) + 1;
  if (days > 31) return 'Range cannot exceed 31 days.';
  return null;
};

/**
 * C3's four presets, computed fresh per render from the MANILA today (C2 —
 * every "today" on this page comes from todayManilaYMD). weekStartsOn: 1 is
 * C3a: the week runs Monday-Sunday (PH working week) — date-fns defaults to
 * Sunday, and nothing else in the repo settles the convention.
 */
const buildPresets = () => {
  const base = parsePeriodToDate(todayManilaYMD());
  const lastWeek = subWeeks(base, 1);
  const lastMonth = subMonths(base, 1);
  const span = (start, end) => ({ start: toYMDString(start), end: toYMDString(end) });
  return [
    { label: 'This week', ...span(startOfWeek(base, { weekStartsOn: 1 }), endOfWeek(base, { weekStartsOn: 1 })) },
    { label: 'Last week', ...span(startOfWeek(lastWeek, { weekStartsOn: 1 }), endOfWeek(lastWeek, { weekStartsOn: 1 })) },
    { label: 'This month', ...span(startOfMonth(base), endOfMonth(base)) },
    { label: 'Last month', ...span(startOfMonth(lastMonth), endOfMonth(lastMonth)) },
  ];
};

const inputStyle = {
  padding: '0.625rem 0.75rem',
  border: '1px solid var(--ui-border)',
  borderRadius: '6px',
  fontSize: '0.875rem',
  background: 'var(--ui-surface)',
  fontFamily: 'var(--font-family-primary)',
};

const labelStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.375rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--ui-text-secondary)',
};

const chipStyle = (active) => ({
  padding: '0.375rem 0.75rem',
  border: `1px solid ${active ? 'var(--ui-primary)' : 'var(--ui-border)'}`,
  borderRadius: '999px',
  fontSize: '0.8125rem',
  fontWeight: 600,
  backgroundColor: active ? 'var(--ui-primary-tint)' : 'var(--ui-surface)',
  color: active ? 'var(--ui-primary)' : 'var(--ui-text-secondary)',
  cursor: 'pointer',
  fontFamily: 'var(--font-family-primary)',
});

const DtrReportPage = () => {
  const { company } = useAuth();
  const toast = useToast();

  // Default range: the current MANILA month (C2), so the default artifact is
  // today's monthly report and the This month chip renders active on load.
  const [fromYMD, setFromYMD] = useState(() => `${todayManilaYMD().slice(0, 7)}-01`);
  const [toYMD, setToYMD] = useState(() =>
    toYMDString(endOfMonth(parsePeriodToDate(todayManilaYMD())))
  );
  const [scope, setScope] = useState('all');
  const [department, setDepartment] = useState('');
  const [employeeDocId, setEmployeeDocId] = useState('');
  const [employees, setEmployees] = useState([]);
  const [rosterError, setRosterError] = useState(false);
  const [generating, setGenerating] = useState(false);
  // Preview before download, shared with the Attendance Summary report
  // (/test-impl 2026-09-08 — the two reports behaved differently and the
  // inconsistency was visible the moment they sat side by side on the hub).
  const { preview, isMobile, showPreview, clearPreview } = usePdfPreview();

  useEffect(() => {
    const fetchRoster = async () => {
      if (!company?.id) return;
      try {
        const employeesRef = collection(db, 'companies', company.id, 'employees');
        const snapshot = await getDocs(query(employeesRef, where('role', '==', 'employee')));
        const list = [];
        snapshot.forEach((d) => list.push({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.employeeId || '').localeCompare(b.employeeId || ''));
        setEmployees(list);
        setRosterError(false);
      } catch (err) {
        console.error('[DtrReportPage] roster read failed:', err);
        setRosterError(true);
      }
    };
    fetchRoster();
  }, [company?.id]);

  const departments = [
    ...new Set(employees.map((e) => e.employment?.department).filter(Boolean)),
  ].sort();

  const presets = buildPresets();
  // Validation is DERIVED, not stored — so a preset click clears any error
  // structurally (C3): setting valid bounds is the clear.
  const dateError = rangeError(fromYMD, toYMD);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      // Load the PDF chunk FIRST so a chunk failure is reported as exactly
      // that (C15) and no reads are spent on a generate that cannot finish.
      let pdf;
      try {
        pdf = await loadDtrPdf();
      } catch (err) {
        console.error('[DtrReportPage] PDF chunk load failed:', err);
        clearPreview();
        toast.error(PDF_LOAD_ERROR);
        return;
      }

      // Scope (C18): batch scopes take everyone employed on ANY day of the
      // range — window overlap, not boundary tests. An explicitly selected
      // single employee always generates (out-of-window days carry markers).
      let scoped;
      if (scope === 'single') {
        scoped = employees.filter((e) => e.id === employeeDocId);
      } else if (scope === 'department') {
        scoped = employees.filter(
          (e) =>
            e.employment?.department === department &&
            isEmployedInRange(e, fromYMD, toYMD)
        );
      } else {
        scoped = employees.filter((e) => isEmployedInRange(e, fromYMD, toYMD));
      }

      if (scoped.length === 0) {
        clearPreview();
        toast.error('No employees in the selected scope for that range.');
        return;
      }

      let models;
      try {
        const today = new Date();
        // C7: a range spans 1-3 month documents (a 31-day range starting Jan
        // 30 touches Jan, Feb AND Mar). The month reads are a Promise.all
        // NESTED inside the per-employee Promise.all — the serial version of
        // this exact read was ProcessPayroll's ~17s resume stall.
        const monthKeys = getMonthsInRange(fromYMD, toYMD);
        models = await Promise.all(
          scoped.map(async (employee) => {
            const snaps = await Promise.all(
              monthKeys.map((monthKey) =>
                getDoc(
                  doc(
                    db,
                    'companies',
                    company.id,
                    'dailyTimeRecords',
                    `${monthKey}_${employee.employeeId}`
                  )
                )
              )
            );
            // A missing document is a valid month of no records — the
            // employee still gets a full-length page (C18).
            const records = snaps.flatMap((snap) =>
              snap.exists() ? snap.data().timeRecords || [] : []
            );
            return buildDtrModel({
              company,
              employee,
              records,
              rangeStartYMD: fromYMD,
              rangeEndYMD: toYMD,
              today,
            });
          })
        );
      } catch (err) {
        console.error('[DtrReportPage] record read failed:', err);
        clearPreview();
        toast.error('Failed to load time records. Please try again.');
        return;
      }

      const payload = models.length === 1 ? models[0] : models;
      // The batch label names the SCOPE, not just the count: two departments
      // with the same headcount would otherwise produce an identical caption
      // beside a live Download button, and the batch filename is scope-blind
      // too (DTR_batch_{stamp}) — so the caption is the only thing telling the
      // two documents apart (/impl-review round 2).
      const scopeLabel =
        models.length === 1
          ? `${models[0].header.employeeId} — ${models[0].header.employeeName}`
          : `${scope === 'department' ? `Department: ${department} · ` : ''}${models.length} employees`;
      showPreview(
        pdf.buildDtrPdf(payload),
        pdf.dtrFileName(payload),
        `${models[0].header.periodLabel} · ${scopeLabel}`
      );
    } finally {
      setGenerating(false);
    }
  };

  const generateDisabled =
    generating ||
    rosterError ||
    employees.length === 0 ||
    dateError !== null ||
    (scope === 'department' && !department) ||
    (scope === 'single' && !employeeDocId);

  return (
    <div>
      <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--ui-primary)', margin: '0 0 0.25rem 0' }}>
        Daily Time Record (DTR)
      </h1>
      <p style={{ fontSize: '0.875rem', color: 'var(--ui-text-secondary)', margin: '0 0 1.5rem 0' }}>
        Printable attendance record for any range up to a month, exactly as the
        Employee Time Records screen shows it.
      </p>

      {rosterError && (
        <div
          style={{
            padding: '0.75rem 1rem',
            marginBottom: '1rem',
            background: 'var(--ui-danger-tint)',
            color: 'var(--ui-danger-text)',
            borderRadius: '8px',
            fontSize: '0.875rem',
          }}
        >
          Failed to load the employee list. Reload the page and try again.
        </div>
      )}

      <div
        style={{
          padding: '1.25rem',
          background: 'var(--ui-surface)',
          border: '1px solid var(--ui-border)',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
          {presets.map((p) => {
            const active = fromYMD === p.start && toYMD === p.end;
            return (
              <button
                key={p.label}
                type="button"
                aria-pressed={active}
                onClick={() => {
                  // A chip PREFILLS the date inputs — they stay fully
                  // editable and remain the single source of truth (C1/C3).
                  setFromYMD(p.start);
                  setToYMD(p.end);
                }}
                style={chipStyle(active)}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: '1rem' }}>
          <label style={labelStyle}>
            FROM
            <input
              type="date"
              value={fromYMD}
              onChange={(e) => setFromYMD(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            TO
            <input
              type="date"
              value={toYMD}
              onChange={(e) => setToYMD(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={labelStyle}>
            SCOPE
            <select value={scope} onChange={(e) => setScope(e.target.value)} style={inputStyle}>
              <option value="all">All employees</option>
              <option value="department">By department</option>
              <option value="single">Single employee</option>
            </select>
          </label>

          {scope === 'department' && (
            <label style={labelStyle}>
              DEPARTMENT
              <select value={department} onChange={(e) => setDepartment(e.target.value)} style={inputStyle}>
                <option value="">Select a department…</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </label>
          )}

          {scope === 'single' && (
            <label style={labelStyle}>
              EMPLOYEE
              <select value={employeeDocId} onChange={(e) => setEmployeeDocId(e.target.value)} style={inputStyle}>
                <option value="">Select an employee…</option>
                {employees.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.employeeId} — {e.personalInfo?.fullName || ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateDisabled}
            style={{
              padding: '0.625rem 1.25rem',
              background: 'var(--ui-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.875rem',
              cursor: generateDisabled ? 'not-allowed' : 'pointer',
              opacity: generateDisabled ? 0.6 : 1,
              fontFamily: 'var(--font-family-primary)',
            }}
          >
            {generating ? 'Generating…' : 'Generate'}
          </button>
        </div>

        {dateError && (
          <p
            style={{
              margin: '0.75rem 0 0 0',
              fontSize: '0.8125rem',
              color: 'var(--ui-danger-text)',
            }}
          >
            {dateError}
          </p>
        )}
      </div>
      <PdfPreviewPanel preview={preview} isMobile={isMobile} title="Daily Time Record preview" />
    </div>
  );
};

export default DtrReportPage;
